// src/features/records/components/RecordDetail/hooks/useComments.js
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  onSnapshot,
  where,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../../../../firebase';

const mapCommentDoc = (docSnap) => ({
  id: docSnap.id,
  ...docSnap.data()
});

const isActiveComment = (comment) => comment?.status !== 'deleted';

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : null;
  }
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const compareCommentsByCreatedAtDesc = (a, b) => {
  const aTime = toMillis(a?.createdAt);
  const bTime = toMillis(b?.createdAt);

  if (aTime != null && bTime != null && aTime !== bTime) {
    return bTime - aTime;
  }
  if (aTime == null && bTime != null) return 1;
  if (aTime != null && bTime == null) return -1;

  const aId = typeof a?.id === 'string' ? a.id : '';
  const bId = typeof b?.id === 'string' ? b.id : '';
  if (aId === bId) return 0;
  return aId < bId ? -1 : 1;
};

const mergeRootComments = (existing, incoming) => {
  const merged = new Map();

  existing.forEach((comment) => {
    const id = comment?.id;
    if (!id) return;
    merged.set(id, comment);
  });

  incoming.forEach((comment) => {
    const id = comment?.id;
    if (!id) return;
    const previous = merged.get(id);
    merged.set(id, previous ? { ...previous, ...comment } : comment);
  });

  return Array.from(merged.values()).sort(compareCommentsByCreatedAtDesc);
};

/**
 * Hook for fetching root comments with pagination and per-thread replies.
 * 
 * @param {string} recordId - The record ID
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Number of root comments per page (default: 20)
 * @param {boolean} options.enableRealtime - Enable real-time updates (default: true)
 * @returns {Object} Comments data and controls
 */
export default function useComments(recordId, options = {}) {
  const { pageSize = 20, enableRealtime = true } = options;

  const [rootComments, setRootComments] = useState([]);
  const [repliesByRootId, setRepliesByRootId] = useState({});
  const [replyPresenceByRootId, setReplyPresenceByRootId] = useState({});
  const [loadingRoots, setLoadingRoots] = useState(true);
  const [loadingMoreRoots, setLoadingMoreRoots] = useState(false);
  const [loadingRepliesByRootId, setLoadingRepliesByRootId] = useState({});
  const [error, setError] = useState(null);
  const [hasMoreRoots, setHasMoreRoots] = useState(true);
  const [lastRootDoc, setLastRootDoc] = useState(null);

  const replyListenersRef = useRef(new Map());
  const replyPresenceRequestsRef = useRef(new Set());
  const rootFetchesRef = useRef(new Map());

  const fetchRootComments = useCallback(async () => {
    if (!recordId) {
      setError(new Error('Invalid record ID'));
      setLoadingRoots(false);
      return;
    }

    try {
      setLoadingRoots(true);
      setError(null);

      const commentsRef = collection(db, 'records', recordId, 'comments');
      const q = query(
        commentsRef,
        where('parentId', '==', null),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      const snapshot = await getDocs(q);
      const fetchedComments = snapshot.docs.map(mapCommentDoc);

      setRootComments(mergeRootComments([], fetchedComments));
      setLastRootDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMoreRoots(snapshot.docs.length === pageSize);
    } catch (err) {
      console.error('Error fetching root comments:', err);
      setError(err);
    } finally {
      setLoadingRoots(false);
    }
  }, [recordId, pageSize]);

  const loadMoreRoots = useCallback(async () => {
    if (!recordId || !lastRootDoc || loadingMoreRoots || !hasMoreRoots) {
      return;
    }

    try {
      setLoadingMoreRoots(true);
      setError(null);

      const commentsRef = collection(db, 'records', recordId, 'comments');
      const q = query(
        commentsRef,
        where('parentId', '==', null),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        startAfter(lastRootDoc),
        limit(pageSize)
      );

      const snapshot = await getDocs(q);
      const fetchedComments = snapshot.docs.map(mapCommentDoc);

      setRootComments((prev) => mergeRootComments(prev, fetchedComments));
      setLastRootDoc(snapshot.docs[snapshot.docs.length - 1] || lastRootDoc);
      setHasMoreRoots(snapshot.docs.length === pageSize);
    } catch (err) {
      console.error('Error loading more root comments:', err);
      setError(err);
    } finally {
      setLoadingMoreRoots(false);
    }
  }, [recordId, lastRootDoc, loadingMoreRoots, hasMoreRoots, pageSize]);

  const upsertRootComment = useCallback((comment) => {
    if (!comment?.id) {
      return;
    }

    setRootComments((prev) => mergeRootComments(prev, [comment]));
  }, []);

  const ensureRootCommentLoaded = useCallback((rootId) => {
    if (!recordId || !rootId) {
      return Promise.resolve(null);
    }

    const existingPromise = rootFetchesRef.current.get(rootId);
    if (existingPromise) {
      return existingPromise;
    }

    const fetchPromise = (async () => {
      try {
        const snap = await getDoc(doc(db, 'records', recordId, 'comments', rootId));
        if (!snap.exists()) {
          return null;
        }
        const data = snap.data() || {};
        if (data.parentId != null || data.status === 'deleted') {
          return null;
        }
        const comment = { id: snap.id, ...data };
        upsertRootComment(comment);
        return comment;
      } catch (err) {
        console.error('Failed to fetch focused root comment:', err);
        return null;
      } finally {
        rootFetchesRef.current.delete(rootId);
      }
    })();

    rootFetchesRef.current.set(rootId, fetchPromise);
    return fetchPromise;
  }, [recordId, upsertRootComment]);

  const loadReplies = useCallback(async (rootId, { realtime = enableRealtime } = {}) => {
    if (!recordId || !rootId) {
      return;
    }

    setLoadingRepliesByRootId((prev) => ({ ...prev, [rootId]: true }));

    const commentsRef = collection(db, 'records', recordId, 'comments');
    const q = query(
      commentsRef,
      where('parentId', '==', rootId),
      where('status', '==', 'active'),
      orderBy('createdAt', 'asc')
    );

    if (realtime) {
      if (replyListenersRef.current.has(rootId)) {
        setLoadingRepliesByRootId((prev) => ({ ...prev, [rootId]: false }));
        return;
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const replies = snapshot.docs.map(mapCommentDoc).filter(isActiveComment);
          setRepliesByRootId((prev) => ({ ...prev, [rootId]: replies }));
          setReplyPresenceByRootId((prev) => ({ ...prev, [rootId]: replies.length > 0 }));
          setLoadingRepliesByRootId((prev) => ({ ...prev, [rootId]: false }));
        },
        (err) => {
          console.error('Error in replies listener:', err);
          setLoadingRepliesByRootId((prev) => ({ ...prev, [rootId]: false }));
        }
      );

      replyListenersRef.current.set(rootId, unsubscribe);
      return;
    }

    try {
      const snapshot = await getDocs(q);
      const replies = snapshot.docs.map(mapCommentDoc).filter(isActiveComment);
      setRepliesByRootId((prev) => ({ ...prev, [rootId]: replies }));
      setReplyPresenceByRootId((prev) => ({ ...prev, [rootId]: replies.length > 0 }));
    } catch (err) {
      console.error('Error fetching replies:', err);
      setError(err);
    } finally {
      setLoadingRepliesByRootId((prev) => ({ ...prev, [rootId]: false }));
    }
  }, [recordId, enableRealtime]);

  const checkReplyPresence = useCallback(async (rootId) => {
    if (!recordId || !rootId) {
      return;
    }

    if (replyPresenceRequestsRef.current.has(rootId)) {
      return;
    }

    replyPresenceRequestsRef.current.add(rootId);

    try {
      const commentsRef = collection(db, 'records', recordId, 'comments');
      const q = query(
        commentsRef,
        where('parentId', '==', rootId),
        where('status', '==', 'active'),
        limit(1)
      );
      const snapshot = await getDocs(q);
      setReplyPresenceByRootId((prev) => ({ ...prev, [rootId]: snapshot.docs.length > 0 }));
    } catch (err) {
      console.error('Error checking replies presence:', err);
    } finally {
      replyPresenceRequestsRef.current.delete(rootId);
    }
  }, [recordId]);

  const markCommentDeleted = useCallback((commentId) => {
    if (!commentId) {
      return;
    }

    setRootComments((prev) => prev.map((comment) => (
      comment.id === commentId ? { ...comment, status: 'deleted' } : comment
    )));

    setRepliesByRootId((prev) => {
      let hasChanges = false;
      const updatedRoots = new Set();
      const next = Object.entries(prev).reduce((acc, [rootId, replies]) => {
        let nextReplies = replies;

        if (rootId === commentId) {
          hasChanges = true;
          updatedRoots.add(rootId);
          nextReplies = [];
        } else {
          const filtered = replies.filter((reply) => reply.id !== commentId);
          if (filtered.length !== replies.length) {
            hasChanges = true;
            updatedRoots.add(rootId);
          }
          nextReplies = filtered;
        }

        acc[rootId] = nextReplies;
        return acc;
      }, {});

      if (!hasChanges) {
        return prev;
      }

      setReplyPresenceByRootId((prevPresence) => {
        const nextPresence = { ...prevPresence };
        updatedRoots.forEach((rootId) => {
          nextPresence[rootId] = (next[rootId] || []).length > 0;
        });
        return nextPresence;
      });

      return next;
    });
  }, []);

  const stopRepliesListener = useCallback((rootId) => {
    const unsubscribe = replyListenersRef.current.get(rootId);
    if (unsubscribe) {
      unsubscribe();
      replyListenersRef.current.delete(rootId);
    }
  }, []);

  // Initial fetch for roots
  useEffect(() => {
    setRootComments([]);
    setRepliesByRootId({});
    setLoadingRepliesByRootId({});
    setReplyPresenceByRootId({});
    rootFetchesRef.current.clear();
    replyPresenceRequestsRef.current.clear();
    setHasMoreRoots(true);
    setLastRootDoc(null);
    fetchRootComments();
  }, [fetchRootComments, recordId]);

  // Real-time updates for root comments (first page)
  useEffect(() => {
    if (!recordId || !enableRealtime) {
      return;
    }

    const commentsRef = collection(db, 'records', recordId, 'comments');
    const q = query(
      commentsRef,
      where('parentId', '==', null),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const updatedRoots = snapshot.docs.map(mapCommentDoc);
        const removedDeletedIds = snapshot
          .docChanges()
          .filter((change) => change.type === 'removed')
          .filter((change) => !change.doc.exists() || change.doc.data()?.status === 'deleted')
          .map((change) => change.doc.id);

        setRootComments((prev) => {
          const merged = mergeRootComments(prev, updatedRoots);
          if (removedDeletedIds.length === 0) {
            return merged;
          }
          const removedSet = new Set(removedDeletedIds);
          return merged.map((comment) => (
            removedSet.has(comment.id) ? { ...comment, status: 'deleted' } : comment
          ));
        });
      },
      (err) => {
        console.error('Error in root comments listener:', err);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [recordId, enableRealtime, pageSize]);

  // Check reply presence for visible root comments
  useEffect(() => {
    if (!recordId || rootComments.length === 0) {
      return;
    }

    rootComments.forEach((comment) => {
      const rootId = comment?.id;
      if (!rootId) {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(replyPresenceByRootId, rootId)) {
        return;
      }

      checkReplyPresence(rootId);
    });
  }, [recordId, rootComments, replyPresenceByRootId, checkReplyPresence]);

  // Cleanup reply listeners on record change/unmount
  useEffect(() => {
    const replyListeners = replyListenersRef.current;
    const replyPresenceRequests = replyPresenceRequestsRef.current;

    return () => {
      replyListeners.forEach((unsubscribe) => unsubscribe());
      replyListeners.clear();
      replyPresenceRequests.clear();
    };
  }, [recordId]);

  return {
    rootComments,
    repliesByRootId,
    replyPresenceByRootId,
    loadingRoots,
    loadingMoreRoots,
    loadingRepliesByRootId,
    error,
    hasMoreRoots,
    loadMoreRoots,
    loadReplies,
    stopRepliesListener,
    markCommentDeleted,
    upsertRootComment,
    ensureRootCommentLoaded,
    refresh: fetchRootComments
  };
}
